import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { getUserDisplay, haptic } from '../lib/telegram';
import BalanceCard from '../components/BalanceCard';
import EntryRow from '../components/EntryRow';
import { formatAmount } from '../lib/format';
import type { Entry, Summary, Preset } from '../lib/types';
import type { Screen } from '../App';

interface Props {
  navigate: (s: Screen) => void;
}

export default function Home({ navigate }: Props) {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [recent, setRecent] = useState<Entry[]>([]);
  const [presets, setPresets] = useState<Preset[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [presetBusy, setPresetBusy] = useState<number | null>(null);

  async function load() {
    setErr(null);
    try {
      const [s, r, p] = await Promise.all([api.summary(), api.list({ limit: 10 }), api.listPresets()]);
      setSummary(s);
      setRecent(r.entries);
      setPresets(p.presets);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed to load');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  function openEdit(entry: Entry) {
    if (entry.kind === 'exchange') {
      // Exchange entries aren't editable — only deletable. Open a confirm flow inline.
      handleQuickDelete(entry);
      return;
    }
    navigate({ name: 'edit', entry });
  }

  async function handleQuickDelete(entry: Entry) {
    try {
      await api.remove(entry.id);
      haptic('success');
      load();
    } catch (e) {
      haptic('error');
      setErr(e instanceof Error ? e.message : 'delete failed');
    }
  }

  async function usePreset(p: Preset) {
    setPresetBusy(p.id);
    try {
      await api.usePreset(p.id);
      haptic('success');
      load();
    } catch (e) {
      haptic('error');
      setErr(e instanceof Error ? e.message : 'failed');
    } finally {
      setPresetBusy(null);
    }
  }

  const isEmpty = summary && summary.balances.USD === 0 && summary.balances.SYP === 0 && recent.length === 0;

  return (
    <>
      <div className="header">
        <h1>Hi {getUserDisplay()}</h1>
        <button className="back" onClick={() => navigate({ name: 'settings' })} aria-label="Settings">⚙️</button>
      </div>
      {err && <div className="error">{err}</div>}

      {isEmpty && (
        <div className="card">
          <h2 style={{ marginTop: 0, fontSize: 18 }}>Welcome 👋</h2>
          <p className="muted small" style={{ marginTop: 0 }}>
            Got cash on hand already? Set your starting balance so your tracker matches reality from day one.
          </p>
          <button className="btn" onClick={() => navigate({ name: 'starting-balance' })}>
            Set starting balance
          </button>
        </div>
      )}

      {summary ? (
        <BalanceCard summary={summary} />
      ) : (
        <div className="card"><div className="muted">{loading ? 'Loading…' : 'No data'}</div></div>
      )}

      <div className="actions" style={{ marginBottom: 16 }}>
        <button className="action-tile income" onClick={() => navigate({ name: 'add', kind: 'income' })}>
          <span className="tile-icon">＋</span>
          Income
        </button>
        <button className="action-tile expense" onClick={() => navigate({ name: 'add', kind: 'expense' })}>
          <span className="tile-icon">−</span>
          Expense
        </button>
        <button className="action-tile exchange" onClick={() => navigate({ name: 'exchange' })}>
          <span className="tile-icon">⇄</span>
          Exchange
        </button>
      </div>

      {(presets.length > 0 || !isEmpty) && (
        <div className="preset-row">
          {presets.map((p) => (
            <button key={p.id} className="preset-chip" onClick={() => usePreset(p)} disabled={presetBusy === p.id}>
              <span className="preset-emoji">{p.emoji || (p.kind === 'income' ? '＋' : '·')}</span>
              <span className="preset-label">{p.label}</span>
              <span className="preset-amount">{formatAmount(Math.abs(Number(p.amount)), p.currency)}</span>
            </button>
          ))}
          <button className="preset-chip add" onClick={() => navigate({ name: 'presets' })}>
            <span className="preset-emoji">＋</span>
            <span className="preset-label">Add</span>
            <span className="preset-amount">preset</span>
          </button>
        </div>
      )}

      <div className="card">
        <div className="section-title">Recent</div>
        {recent.length === 0 ? (
          <div className="empty">No entries yet. Log your first income or expense.</div>
        ) : (
          recent.map((e) => <EntryRow key={e.id} entry={e} onTap={openEdit} />)
        )}
      </div>

      {!isEmpty && (
        <button
          className="btn btn-secondary"
          style={{ marginTop: 8 }}
          onClick={() => navigate({ name: 'cash-counter' })}
        >
          🧮 Cash counter
        </button>
      )}

      {!isEmpty && (
        <button
          className="btn btn-secondary"
          style={{ marginTop: 8 }}
          onClick={() => navigate({ name: 'reconcile' })}
        >
          ⚖️ Reconcile balance
        </button>
      )}
    </>
  );
}
